import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '@/services/api'
import { useTranslation } from 'react-i18next'
import Spinner from '@/components/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUsers, faMusic, faCalendarDays, faDollarSign, faArrowRight, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons'

interface Stats {
  summary: {
    totalUsers: number
    totalEvents: number
    totalSingers: number
    totalSongsInQueue: number
    totalSongsSung: number
    uniqueOrganizers: number
  }
  usersByRole: Array<{ role: string; count: number }>
  eventsByStatus: Array<{ status: string; count: number }>
  subscriptionsByType: Array<{ type: string; count: number }>
}

interface RevenueStats {
  totalRevenue: number
  totalPaymentsCount: number
  thisMonthRevenue: number
  thisMonthCount: number
  thisQuarterRevenue: number
  thisYearRevenue: number
  mrr: number
  arr: number
  averageRevenuePerPayment: number
}

/**
 * SuperAdmin Dashboard with key statistics
 * Dark mode geek aesthetic
 */
export default function DashboardPage() {
  const { t } = useTranslation()
  const [stats, setStats] = useState<Stats | null>(null)
  const [revenueStats, setRevenueStats] = useState<RevenueStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [revenueLoading, setRevenueLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/superadmin/stats')
        if (response.data.success) {
          setStats(response.data.data)
        }
      } catch (err: any) {
        setError(err.response?.data?.error || t('common.error'))
      } finally {
        setLoading(false)
      }
    }

    const fetchRevenueStats = async () => {
      try {
        const response = await api.get('/superadmin/revenue/stats')
        if (response.data.success) {
          setRevenueStats(response.data.data)
        }
      } catch (err: any) {
        // Silently handle revenue error - fallback to €0.00
        console.error('Failed to fetch revenue stats:', err)
        setRevenueStats(null)
      } finally {
        setRevenueLoading(false)
      }
    }

    fetchStats()
    fetchRevenueStats()
  }, [t])

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4">
        <p className="text-red-300 font-mono text-sm">
          <span className="text-red-500">{'[ERROR]'}</span> {error}
        </p>
      </div>
    )
  }

  // Format revenue amount in French format
  const formatRevenue = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '€0.00'
    }
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  const statCards = [
    {
      label: t('superadmin.totalOrganizers'),
      value: stats?.summary?.uniqueOrganizers || 0,
      icon: faUsers,
      gradient: 'from-cyan-600 to-cyan-500',
      color: 'text-cyan-400',
    },
    {
      label: t('superadmin.totalSingers'),
      value: stats?.summary?.totalSingers || 0,
      icon: faMusic,
      gradient: 'from-purple-600 to-purple-500',
      color: 'text-purple-400',
    },
    {
      label: t('superadmin.totalEvents'),
      value: stats?.summary?.totalEvents || 0,
      icon: faCalendarDays,
      gradient: 'from-pink-600 to-pink-500',
      color: 'text-pink-400',
    },
    {
      label: t('superadmin.totalRevenue'),
      value: revenueLoading ? '—' : formatRevenue(revenueStats?.totalRevenue),
      icon: faDollarSign,
      gradient: 'from-green-600 to-green-500',
      color: 'text-green-400',
    },
  ]

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-4xl font-black bg-linear-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
          {t('superadmin.dashboard')}
        </h1>
        <p className="text-slate-400 mt-2 font-mono text-sm">
          {'> '} <span className="text-cyan-400">stats</span>{' --all'}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-6">
        {statCards.map((card, idx) => (
          <div
            key={idx}
            className="group relative bg-slate-900 border border-slate-800 rounded-xl p-6 hover:border-slate-700 transition overflow-hidden"
          >
            {/* Hover gradient background */}
            <div className={`absolute inset-0 bg-linear-to-br ${card.gradient} opacity-0 group-hover:opacity-5 transition`} />
            
            <div className="relative flex items-start justify-between">
              <div className="flex-1">
                <p className="text-slate-400 text-xs font-mono uppercase tracking-wider">
                  {card.label}
                </p>
                <p className="text-4xl font-black text-white mt-3">
                  {card.value}
                </p>
              </div>
              <div className={`p-3 rounded-lg bg-linear-to-br ${card.gradient}`}>
                <FontAwesomeIcon icon={card.icon} className="text-white text-xl" />
              </div>
            </div>

            {/* Sparkline */}
            <div className="mt-4 flex items-end gap-1 h-8 opacity-50">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 bg-linear-to-t ${card.gradient} rounded-t opacity-70`}
                  style={{
                    height: `${20 + Math.sin(i) * 10}px`,
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions Section */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white mb-4 font-mono">
          {'> '}<span className="text-cyan-400">actions</span>
        </h2>
        <div className="grid grid-cols-5 gap-4">
          <Link
            to="/organizers"
            className="group px-4 py-3 bg-linear-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 text-white rounded-lg transition text-center font-bold text-sm uppercase tracking-wider hover:shadow-lg hover:shadow-cyan-500/30"
          >
            {t('superadmin.manageOrganizers')}
            <FontAwesomeIcon icon={faArrowRight} className="ml-2 opacity-0 group-hover:opacity-100 transition" />
          </Link>
          <Link
            to="/events"
            className="group px-4 py-3 bg-linear-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white rounded-lg transition text-center font-bold text-sm uppercase tracking-wider hover:shadow-lg hover:shadow-amber-500/30"
          >
            Gérer les événements
            <FontAwesomeIcon icon={faArrowRight} className="ml-2 opacity-0 group-hover:opacity-100 transition" />
          </Link>
          <Link
            to="/venues"
            className="group px-4 py-3 bg-linear-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-lg transition text-center font-bold text-sm uppercase tracking-wider hover:shadow-lg hover:shadow-blue-500/30"
          >
            Gérer les lieux
            <FontAwesomeIcon icon={faArrowRight} className="ml-2 opacity-0 group-hover:opacity-100 transition" />
          </Link>
          <Link
            to="/singers"
            className="group px-4 py-3 bg-linear-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-lg transition text-center font-bold text-sm uppercase tracking-wider hover:shadow-lg hover:shadow-purple-500/30"
          >
            {t('superadmin.manageSingers')}
            <FontAwesomeIcon icon={faArrowRight} className="ml-2 opacity-0 group-hover:opacity-100 transition" />
          </Link>
          <button
            className="px-4 py-3 bg-slate-800 text-slate-400 rounded-lg transition text-center font-bold text-sm uppercase tracking-wider opacity-50 cursor-not-allowed"
            disabled
          >
            {t('superadmin.viewReports')} (Soon)
          </button>
        </div>
      </div>

      {/* System Info Footer */}
      <div className="bg-slate-900/50 border border-slate-800/50 rounded-lg p-4 font-mono text-xs text-slate-500">
        <p>
          {'> '}<span className="text-slate-400">system</span>{' --info'}<br />
          Platform: KaraoNow v1.0 | LastUpdate: {new Date().toLocaleString('fr-FR')}
        </p>
      </div>
    </div>
  )
}

